/**
 * Lista de documentos que o aluno enviou (PDF ou imagem). Clicar
 * abre via `Linking.openURL` em browser/visualizador externo.
 *
 * Props:
 *   - studentId: usado no hook useStudentDocuments
 *
 * Estado interno: usa `useStudentDocuments(studentId)` — fetcha do
 * Supabase Storage (bucket student-documents). RLS garante que só
 * o personal dono vê.
 *
 * Decisões:
 *   - Ícone muda por tipo: 'document-outline' pra PDF, 'image-outline'
 *     pro resto (jpg/png).
 *   - Sem preview inline pra economizar bandwidth — só link externo.
 */

import { memo } from 'react';
import { View, Text, TouchableOpacity, Linking, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStudentDocuments } from '@/hooks/useStudentDocuments';
import { sharedStyles } from './styles';

interface Props {
  studentId: string;
}

function DocumentsSectionImpl({ studentId }: Props) {
  const { documents } = useStudentDocuments(studentId);

  return (
    <View style={sharedStyles.section}>
      <Text style={sharedStyles.sectionTitle}>Documentos</Text>
      {documents.length === 0 ? (
        <Text style={sharedStyles.empty}>Nenhum documento enviado pelo aluno.</Text>
      ) : (
        documents.map(doc => (
          <TouchableOpacity
            key={doc.id}
            style={styles.docRow}
            onPress={() => Linking.openURL(doc.file_url)}
            activeOpacity={0.7}
          >
            <Ionicons
              name={doc.file_type === 'pdf' ? 'document-outline' : 'image-outline'}
              size={20}
              color="#C8102E"
            />
            <View style={{ flex: 1 }}>
              <Text style={styles.docName} numberOfLines={1}>{doc.name}</Text>
              <Text style={styles.docDate}>
                {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
              </Text>
            </View>
            <Ionicons name="open-outline" size={16} color="#aaa" />
          </TouchableOpacity>
        ))
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  docRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 12,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#eee',
  },
  docName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  docDate: { fontSize: 11, color: '#888', marginTop: 2 },
});

export const DocumentsSection = memo(DocumentsSectionImpl);
